import React, { useCallback, useMemo } from "react";
import { WahooWorkoutTypeFamily } from "@WahooFitness/cloud-client-types";
import {
  MenuList,
  MenuListItemType,
  MenuListItemVariant,
  WfIconNavigationChevronRightRegular,
  WfIconSportAirBold,
  WfIconSportBoltFill,
  WfIconSportHeartFill,
  WfIconSportSpeedFill,
} from "@WahooFitness/redesignr";
import useZones from "@/components/Zones/useZones";
import ZoneSelect from "./ZoneSelect";
import useHasElemntWithTymewear from "@/hooks/useHasElemntWithTymewear";
import { useWorkoutProfilesContext } from "@/hooks/useWorkoutProfilesContext";
import { t, Trans } from "@lingui/macro";
import { ReactRouterLinkWrapper } from "@/components/MenuList/ReactRouterLinkWrapper";
import { useDrawerContext } from "@WahooFitness/wahoo-offline-mfe";
import { WSMWorkoutProfile } from "@WahooFitness/wsm-native/dist/esm/types/workout_profile";
import { Box, Typography, useTheme } from "@mui/material";

interface Props {
  profile?: WSMWorkoutProfile;
}

const ZonesSection: React.FC<Props> = ({ profile }) => {
  const { setDrawer, handleClose } = useDrawerContext();
  const { zoneSets: powerZoneSets, isLoading: powerIsLoading } = useZones("power");
  const { zoneSets: hrZoneSets, isLoading: hrIsLoading } = useZones("heartRate");
  const { zoneSets: paceZoneSets, isLoading: paceIsLoading } = useZones("speed");
  const { zoneSets: ventilationZoneSets, isLoading: ventilationIsLoading } =
    useZones("ventilation");
  const hasElemntWithTymewear = useHasElemntWithTymewear();

  const { updateProfile } = useWorkoutProfilesContext();
  const { palette } = useTheme();
  const { powerZoneName, hrZoneName, paceZoneName, ventilationZoneName } = useMemo(() => {
    const powerZoneName = profile?.power_zone_id ? powerZoneSets[profile.power_zone_id]?.name : "";
    const hrZoneName = profile?.heart_rate_zone_id
      ? hrZoneSets[profile.heart_rate_zone_id]?.name
      : "";
    const primaryPaceZoneName = Object.values(paceZoneSets).find(
      (zoneSet) => !!zoneSet.primary
    )?.name;
    const paceZoneName = profile?.pace_zone_id
      ? paceZoneSets[profile.pace_zone_id]?.name
      : primaryPaceZoneName;
    const primaryVentilationZoneName = Object.values(ventilationZoneSets).find(
      (zoneSet) => !!zoneSet.primary
    )?.name;
    const ventilationZoneName = profile?.ventilation_zone_id
      ? ventilationZoneSets[profile.ventilation_zone_id]?.name
      : primaryVentilationZoneName;
    return { powerZoneName, hrZoneName, paceZoneName, ventilationZoneName };
  }, [hrZoneSets, paceZoneSets, powerZoneSets, ventilationZoneSets, profile]);

  const zoneDrawerValues = useMemo(
    () => ({
      hr: {
        title: t`Heart rate zone`,
        zoneChoice: profile?.heart_rate_zone_id,
        zoneSets: hrZoneSets,
      },
      power: {
        title: t`Power zone`,
        zoneChoice: profile?.power_zone_id,
        zoneSets: powerZoneSets,
      },
      speed: {
        title: `Running pace zone`,
        zoneChoice: profile?.pace_zone_id,
        zoneSets: paceZoneSets,
      },
      ventilation: {
        title: t`Ventilation zone`,
        zoneChoice: profile?.ventilation_zone_id,
        zoneSets: ventilationZoneSets,
      },
    }),
    [
      hrZoneSets,
      paceZoneSets,
      powerZoneSets,
      ventilationZoneSets,
      profile?.heart_rate_zone_id,
      profile?.pace_zone_id,
      profile?.power_zone_id,
      profile?.ventilation_zone_id,
    ]
  );

  const handleChange = useCallback(
    (
      event: React.ChangeEvent<HTMLInputElement>,
      zoneDrawerMode: "hr" | "power" | "speed" | "ventilation"
    ) => {
      const zoneId = +(event.target as HTMLInputElement).value;
      handleClose();
      if (!profile) {
        return;
      }
      if (zoneDrawerMode === "hr") {
        updateProfile(profile.external_id, "heart_rate_zone_id", zoneId);
      } else if (zoneDrawerMode === "power") {
        updateProfile(profile.external_id, "power_zone_id", zoneId);
      } else if (zoneDrawerMode === "speed") {
        updateProfile(profile.external_id, "pace_zone_id", zoneId);
      } else if (zoneDrawerMode === "ventilation") {
        updateProfile(profile.external_id, "ventilation_zone_id", zoneId);
      }
    },
    [profile, updateProfile, handleClose]
  );

  const openZoneSelect = useCallback(
    (mode: "hr" | "power" | "speed" | "ventilation") => {
      setDrawer({
        open: true,
        title: zoneDrawerValues[mode].title,
        footer: <></>,
        zIndex: 1301,
        children: (
          <ZoneSelect
            handleChange={(ev) => handleChange(ev, mode)}
            zoneChoice={zoneDrawerValues[mode].zoneChoice}
            zoneSets={zoneDrawerValues[mode].zoneSets}
          />
        ),
      });
    },
    [handleChange, setDrawer, zoneDrawerValues]
  );

  const sectionItems = useMemo(() => {
    const baseItems: MenuListItemType[] = [
      {
        id: "powerZone",
        content: t`Power zones`,
        variant: MenuListItemVariant.Action,
        actionComponent: (
          <Box display="flex">
            <Typography variant="prose-base">
              <Trans>{powerZoneName}</Trans>
            </Typography>
            <WfIconNavigationChevronRightRegular sx={{ color: "action.active" }} />
          </Box>
        ),
        action: () => {
          openZoneSelect("power");
        },
        icon: <WfIconSportBoltFill sx={{ fill: palette.data.power.main }} />,
      },
      {
        id: "heartRateZone",
        content: t`Heart rate zones`,
        variant: MenuListItemVariant.Action,
        actionComponent: (
          <Box display="flex">
            <Typography variant="prose-base">
              <Trans>{hrZoneName}</Trans>
            </Typography>
            <WfIconNavigationChevronRightRegular sx={{ color: "action.active" }} />
          </Box>
        ),
        action: () => {
          openZoneSelect("hr");
        },
        icon: <WfIconSportHeartFill sx={{ fill: palette.data.heartRate.main }} />,
      },
    ];
    const noPaceZonesListItem = {
      id: "noPaceZones",
      content: t`Running pace zone`,
      secondaryContent: t`Click here to create pace zones`,
      variant: MenuListItemVariant.InternalLink,
      linkLocation: "/zones/pace-zones",
      linkComponent: ReactRouterLinkWrapper,
    };
    const paceZoneItem = {
      id: "paceZone",
      content: t`Running pace zone`,
      variant: MenuListItemVariant.Action,
      actionComponent: (
        <Box display="flex">
          <Typography variant="prose-base">
            <Trans>{paceZoneName}</Trans>
          </Typography>
          <WfIconNavigationChevronRightRegular sx={{ color: "action.active" }} />
        </Box>
      ),
      action: () => {
        openZoneSelect("speed");
      },
      icon: <WfIconSportSpeedFill sx={{ fill: palette.data.speed.main }} />,
    };
    const renderedPaceZoneListItem = paceZoneName ? paceZoneItem : noPaceZonesListItem;
    if (profile?.workout_type_family_id === WahooWorkoutTypeFamily.Running) {
      baseItems.push(renderedPaceZoneListItem);
    }
    // DEX-788: ventilation has no cloud-provided primary, so until the user creates a set
    // there is nothing to select — link them to the editor instead of an empty selector.
    const noVentilationZonesListItem = {
      id: "noVentilationZones",
      content: t`Ventilation zone`,
      secondaryContent: t`Click here to create ventilation zones`,
      variant: MenuListItemVariant.InternalLink,
      linkLocation: "/zones/ventilation-zones",
      linkComponent: ReactRouterLinkWrapper,
    };
    const ventilationZoneItem = {
      id: "ventilationZone",
      content: t`Ventilation zone`,
      variant: MenuListItemVariant.Action,
      actionComponent: (
        <Box display="flex">
          <Typography variant="prose-base">
            <Trans>{ventilationZoneName}</Trans>
          </Typography>
          <WfIconNavigationChevronRightRegular sx={{ color: "action.active" }} />
        </Box>
      ),
      action: () => {
        openZoneSelect("ventilation");
      },
      icon: <WfIconSportAirBold sx={{ fill: palette.zones.ventilationZones.zone1.main }} />,
    };
    if (hasElemntWithTymewear) {
      baseItems.push(ventilationZoneName ? ventilationZoneItem : noVentilationZonesListItem);
    }
    return baseItems;
  }, [
    powerZoneName,
    palette.data.power.main,
    palette.data.heartRate.main,
    palette.data.speed.main,
    palette.zones.ventilationZones.zone1.main,
    hrZoneName,
    paceZoneName,
    ventilationZoneName,
    hasElemntWithTymewear,
    profile?.workout_type_family_id,
    openZoneSelect,
  ]);

  if (
    powerIsLoading ||
    hrIsLoading ||
    paceIsLoading ||
    (hasElemntWithTymewear && ventilationIsLoading)
  ) {
    return null;
  }

  return <MenuList title={t`Zones`} variant="elevation" listItems={sectionItems} />;
};

export default ZonesSection;
